import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import LoginScreen from "./adminloginscreen.tsx";
import { postWinner } from "../../API/index.ts";
import DetailsModal from "./Detailsmodal.tsx";

ChartJS.register(BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

const API_BASE = "https://wasty-bali.onrender.com";

const ITEMS_PER_PAGE = 10;

const AdminPanel = () => {
  const [reports, setReports] = useState([]);
  const [owners, setOwners] = useState([]);
  const [winner, setWinner] = useState(null);
  const [winnerInput, setWinnerInput] = useState("");
  const [winners, setWinners] = useState([]);
const [showWinnersModal, setShowWinnersModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // --- New State Variables ---
  const [winnerType, setWinnerType] = useState("food");
  const [winnerDate, setWinnerDate] = useState(new Date().toISOString().split("T")[0]);
  const [filters, setFilters] = useState({
    ownerName: "",
    phoneNumber: "",
    shopName: "",
    sortByScore: "desc",
    date: new Date().toISOString().split("T")[0],
  });
  const [isloggedIn, setIsloggedIn] = useState(false);

  // Modals
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showOwnersModal, setShowOwnersModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  // Pagination
  const [reportPage, setReportPage] = useState(1);
  const [ownerPage, setOwnerPage] = useState(1);

  const paginatedReports = reports.slice((reportPage - 1) * ITEMS_PER_PAGE, reportPage * ITEMS_PER_PAGE);
  const paginatedOwners = owners.slice((ownerPage - 1) * ITEMS_PER_PAGE, ownerPage * ITEMS_PER_PAGE);
  const downloadPDF = async (elementId, fileName) => {
    const input = document.getElementById(elementId);
    if (!input) return;
    const canvas = await html2canvas(input, { scale: 2 });
    const pdf = new jsPDF("p", "mm", "a4");
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(fileName);
  };

const fetchAllWinners = async () => {
  try {
    setLoading(true);
    const res = await axios.get(`${API_BASE}/winner/winners/all`, {
      headers: { Accept: "application/json" },
    });
    setWinners(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    console.error("Error fetching winners:", err);
    setError("Failed to load winners.");
  } finally {
    setLoading(false);
  }
};



  // ========= API HANDLERS =========
  const fetchReports = async () => {
    try {
      setLoading(true);
      const { ownerName, phoneNumber, shopName, sortByScore, date } = filters;
      const res = await axios.post(
        `${API_BASE}/admin/form/list?owner_name=${ownerName}&phone_number=${phoneNumber}&shop_name=${shopName}&sort_by_score=${sortByScore}&date=${date}`
      );
      setReports(Array.isArray(res.data.forms) ? res.data.forms : []);
    } catch (err) {
      setError("Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }; 

  const fetchOwners = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/owners`);
      setOwners(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to load shop owners.");
    } finally {
      setLoading(false);
    }
  };

const fetchTodayWinner = async (dateParam) => {
  try {
    const date = dateParam || new Date().toISOString().split("T")[0];
    const res = await axios.post(`${API_BASE}/winner/today?date=${date}`);
    setWinner(res.data?.[0] || null);
  } catch {
    setError("Failed to fetch winner for selected date.");
  }
};

// --- Updated handlePostWinner ---
const handlePostWinner = async () => {
  if (!winnerInput.trim()) return alert("Enter phone number");

  try {
    setLoading(true);

    await postWinner(
      winnerInput.trim(),
      "", // winnerDetails left empty for now
      winnerType, // "food" or "nonfood"
      new Date(winnerDate).toISOString().split("T")[0] // format: YYYY-MM-DD
    );

    alert(`Winner (${winnerType}) posted successfully for ${winnerDate}!`);
    setWinnerInput("");
    fetchTodayWinner(winnerDate);
  } catch (error) {
    alert("Failed to post winner.");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    if (isloggedIn) fetchTodayWinner();
  }, [isloggedIn]);

  // ========= PDF EXPORT (Full Data) =========
  const downloadFullPDF = async (type: "reports" | "owners") => {
    const data = type === "reports" ? reports : owners;
    const title = type === "reports" ? "Reports List" : "Shop Owners List";
    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFontSize(18);
    pdf.text(title, 10, 10);
    pdf.setFontSize(10);

    let y = 20;
    data.forEach((item, i) => {
      if (y > 280) {
        pdf.addPage();
        y = 20;
      }
      if (type === "reports") {
        const d = JSON.parse(item.form_details || "{}");
        pdf.text(
          `${i + 1}. ${d.shopName || "-"} | ${item.shop_owner?.owner_name || "-"} | ${
            item.shop_owner?.phone_number || "-"
          } | Score: ${item.score ?? "-"} | ${new Date(
            d.inspectionDate
          ).toLocaleDateString("en-IN")}`,
          10,
          y
        );
      } else {
        pdf.text(
          `${i + 1}. ${item.owner_name || "-"} | ${item.shop_name || "-"} | ${item.phone_number || "-"}`,
          10,
          y
        );
      }
      y += 7;
    });

    pdf.save(`${title}.pdf`);
  };

  // ========= DASHBOARD CALCS =========
  const averageScore =
    reports.length > 0
      ? (reports.reduce((sum, r) => sum + (r.score || 0), 0) / reports.length).toFixed(1)
      : "—";

  // ========= MAIN UI =========
  if (!isloggedIn) return <LoginScreen onLogin={() => setIsloggedIn(true)} />;

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col items-center py-10 px-6">
      {/* ===== Dashboard Summary ===== */}
      <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-6xl mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-indigo-700">📊 Daily Dashboard</h2>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            className="border px-3 py-2 rounded-lg"
          />
        </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
  <div className="bg-indigo-50 p-4 rounded-xl text-center shadow-sm">
    <p className="text-sm text-gray-600">Selected Date</p>
    <h3 className="text-xl font-bold text-indigo-700">{filters.date}</h3>
  </div>

  <div className="bg-green-50 p-4 rounded-xl text-center shadow-sm">
    <p className="text-sm text-gray-600">Total Shops</p>
    <h3 className="text-xl font-bold text-green-700">{owners.length}</h3>
  </div>

  <div className="bg-yellow-50 p-4 rounded-xl text-center shadow-sm">
    <p className="text-sm text-gray-600">Total Users</p>
    <h3 className="text-xl font-bold text-yellow-700">{owners.length}</h3>
  </div>

  <div className="bg-blue-50 p-4 rounded-xl text-center shadow-sm">
    <p className="text-sm text-gray-600">Total Reports</p>
    <h3 className="text-xl font-bold text-blue-700">{reports.length}</h3>
  </div>

  <div className="bg-purple-50 p-4 rounded-xl text-center shadow-sm">
    <p className="text-sm text-gray-600">Average Score</p>
    <h3 className="text-xl font-bold text-purple-700">{averageScore}</h3>
  </div>
</div>
 

        <div className="flex justify-center gap-6 mt-8">
          <button
            onClick={() => {
              fetchReports();
              setShowReportsModal(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl shadow-md font-semibold"
          >
            📋 View Reports
          </button>
          <button
            onClick={() => {
              fetchOwners();
              setShowOwnersModal(true);
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-md font-semibold"
          >
            🏪 View Shop Owners
          </button>
          <button
  onClick={() => {
    fetchAllWinners();
    setShowWinnersModal(true);
  }}
  className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-xl shadow-md font-semibold"
>
  🏅 View All Winners
</button>
        </div>
      </div>
  {/* 🏆 Winner Card */}
      <div id="winner-card" className="w-full max-w-4xl bg-white p-6 rounded-2xl shadow-lg">
        <h2 className="text-2xl font-bold text-indigo-700 mb-4">🏆 Today's Winner</h2>
<p className="text-gray-600 text-sm mt-2">
  🏷 Type: {winner?.type || "—"} | 📅 {winner?.awarded_at ? new Date(winner?.awarded_at).toLocaleDateString() : winnerDate}
</p>

      <div className="flex flex-col sm:flex-row gap-4 mb-6 flex-wrap">
  <input
    type="text"
    placeholder="Enter Winner's Phone Number"
    value={winnerInput}
    onChange={(e) => setWinnerInput(e.target.value)}
    className="border border-gray-300 rounded-lg px-4 py-2 flex-grow min-w-[200px]"
  />

  <select
    value={winnerType}
    onChange={(e) => setWinnerType(e.target.value)}
    className="border border-gray-300 rounded-lg px-4 py-2 min-w-[120px]"
  >
    <option value="food">Food</option>
    <option value="nonfood">Non-Food</option>
  </select>

  <input
    type="date"
    value={winnerDate}
    onChange={(e) => setWinnerDate(e.target.value)}
    className="border border-gray-300 rounded-lg px-4 py-2 min-w-[160px]"
  />

  <button
    onClick={handlePostWinner}
    className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-lg font-medium"
  >
    Submit Winner
  </button>

  <button
    onClick={() => fetchTodayWinner(winnerDate)}
    className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg font-medium"
  >
    Reload
  </button>
   </div>


        {winner && winner.shop_owner ? (
          <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-6 text-center shadow-sm">
            <img
              src={winner.shop_owner.photo_url}
              alt={winner.shop_owner.owner_name}
              className="w-24 h-24 mx-auto rounded-full border-4 border-indigo-300 mb-3"
            />
            <h3 className="text-lg font-bold text-gray-800">{winner.shop_owner.owner_name}</h3>
            <p className="text-gray-700">{winner.shop_owner.shop_name}</p>
            <p className="text-gray-600 text-sm">📞 {winner.shop_owner.phone_number}</p>
            <p className="text-indigo-700 font-semibold mt-2">
              ⭐ Score: {winner.score ?? "N/A"}
            </p>
            <button
              onClick={() => downloadPDF("winner-card", "Winner_Report.pdf")}
              className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700"
            >
              Download Winner Report
            </button>
          </div>
        ) : (
          <p className="text-gray-500 text-center">No winner selected yet today.</p>
        )}
      </div>

      {/* ===== Reports Modal ===== */}
      {showReportsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-6 relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-indigo-700">Reports</h2>
              <div className="flex gap-2">
                <input
                  placeholder="Search Name/Number"
                  value={filters.ownerName}
                  onChange={(e) =>
                    setFilters({ ...filters, ownerName: e.target.value })
                  }
                  className="border px-3 py-1 rounded"
                />
                <button
                  onClick={() => fetchReports()}
                  className="bg-indigo-600 text-white px-4 py-1 rounded"
                >
                  Search
                </button>
                <button
                  onClick={() => downloadFullPDF("reports")}
                  className="bg-purple-600 text-white px-4 py-1 rounded"
                >
                  📄 Download PDF
                </button>
                <button
                  onClick={() => setShowReportsModal(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ✖
                </button>
              </div>
            </div>

            <table className="w-full border-collapse text-sm">
              <thead className="bg-indigo-100 text-gray-800 font-semibold">
                <tr>
                  <th className="p-2 text-left">Shop</th>
                  <th className="p-2 text-left">Owner</th>
                  <th className="p-2 text-left">Phone</th>
                  <th className="p-2 text-left">Score</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((r, i) => {
                  const d = JSON.parse(r.form_details || "{}");
                  return (
                    <tr
                      key={i}
                      onClick={() => setDetailModal(r)}
                      className="border-t hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="p-2">{d.shopName}</td>
                      <td className="p-2">{r.shop_owner?.owner_name}</td>
                      <td className="p-2">{r.shop_owner?.phone_number}</td>
                      <td className="p-2">{r.score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-between mt-4">
              <button
                disabled={reportPage === 1}
                onClick={() => setReportPage((p) => p - 1)}
                className="px-3 py-1 border rounded"
              >
                Prev
              </button>
              <span>Page {reportPage}</span>
              <button
                disabled={reportPage * ITEMS_PER_PAGE >= reports.length}
                onClick={() => setReportPage((p) => p + 1)}
                className="px-3 py-1 border rounded"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Owners Modal ===== */}
      {showOwnersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-6 relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-green-700">Shop Owners</h2>
              <div className="flex gap-2">
                <input
                  placeholder="Search Name/Number"
                  value={filters.ownerName}
                  onChange={(e) =>
                    setFilters({ ...filters, ownerName: e.target.value })
                  }
                  className="border px-3 py-1 rounded"
                />
                <button
                  onClick={() => fetchOwners()}
                  className="bg-green-600 text-white px-4 py-1 rounded"
                >
                  Search
                </button>
                <button
                  onClick={() => downloadFullPDF("owners")}
                  className="bg-purple-600 text-white px-4 py-1 rounded"
                >
                  📄 Download PDF
                </button>
                <button
                  onClick={() => setShowOwnersModal(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ✖
                </button>
              </div>
            </div>

            <table className="w-full border-collapse text-sm">
            <thead className="bg-green-100 text-gray-800 font-semibold">
  <tr>
    <th className="p-2 text-left w-12">#</th>
    <th className="p-2 text-left">Photo</th>
    <th className="p-2 text-left">Name</th>
    <th className="p-2 text-left">Shop</th>
    <th className="p-2 text-left">Phone</th>
  </tr>
</thead>

            <tbody>
  {paginatedOwners.map((o, i) => (
    <tr
      key={i}
      onClick={() => setDetailModal(o)}
      className="border-t hover:bg-gray-50 cursor-pointer"
    >
      <td className="p-2 text-gray-700 font-medium">
        {(ownerPage - 1) * ITEMS_PER_PAGE + i + 1}
      </td>
      <td className="p-2">
        {o.photo_url ? (
          <img
            src={o.photo_url}
            alt={o.owner_name}
            className="w-10 h-10 rounded-full"
          />
        ) : (
          "N/A"
        )}
      </td>
      <td className="p-2">{o.owner_name}</td>
      <td className="p-2">{o.shop_name}</td>
      <td className="p-2">{o.phone_number}</td>
    </tr>
  ))}
</tbody>

            </table>

            {/* Pagination */}
            <div className="flex justify-between mt-4">
              <button
                disabled={ownerPage === 1}
                onClick={() => setOwnerPage((p) => p - 1)}
                className="px-3 py-1 border rounded"
              >
                Prev
              </button>
              <span>Page {ownerPage}</span>
              <button
                disabled={ownerPage * ITEMS_PER_PAGE >= owners.length}
                onClick={() => setOwnerPage((p) => p + 1)}
                className="px-3 py-1 border rounded"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
{/* ===== Winners Modal ===== */}
{showWinnersModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-6 relative overflow-y-auto max-h-[90vh]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-yellow-700">🏅 All Winners</h2>
        <div className="flex gap-2">
          <button
            onClick={() => fetchAllWinners()}
            className="bg-yellow-600 text-white px-4 py-1 rounded"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowWinnersModal(false)}
            className="text-gray-600 hover:text-gray-800"
          >
            ✖
          </button>
        </div>
      </div>

      {winners.length === 0 ? (
        <p className="text-gray-500 text-center">No winners found.</p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead className="bg-yellow-100 text-gray-800 font-semibold">
            <tr>
              <th className="p-2 text-left w-12">#</th>
              <th className="p-2 text-left">Photo</th>
              <th className="p-2 text-left">Owner</th>
              <th className="p-2 text-left">Shop</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Score</th>
            </tr>
          </thead>
          <tbody>
            {winners.map((w, i) => {
  const forms = Array.isArray(w.shop_owner?.forms) ? w.shop_owner.forms : [];

  // helper: safe parse JSON
  const safeParse = (s) => {
    try { return JSON.parse(s); } catch (e) { return null; }
  };

  // helper: normalize any timestamp into 'YYYY-MM-DD' using local timezone (en-CA gives ISO-like date)
  const toYMD = (ts) => {
    if (!ts) return null;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-CA'); // YYYY-MM-DD in most environments
  };

  const awardedYMD = toYMD(w.awarded_at);

  // Build enriched forms with parsed inspectionDate and createdYMD
  const enriched = forms.map((f) => {
    const details = typeof f.form_details === 'string' ? safeParse(f.form_details) : (f.form_details || null);
    const inspectionDate = details?.inspectionDate || details?.inspection?.inspectionDate || details?.inspectionDate; // defensive
    return {
      ...f,
      createdYMD: toYMD(f.created_at),
      inspectionYMD: toYMD(inspectionDate),
      createdTs: f.created_at ? new Date(f.created_at).getTime() : null
    };
  });

  // 1) prefer inspectionDate exact match
  let matched = enriched.find(f => f.inspectionYMD && f.inspectionYMD === awardedYMD);

  // 2) then prefer created_at exact match
  if (!matched) matched = enriched.find(f => f.createdYMD && f.createdYMD === awardedYMD);

  // 3) if still not found, pick the form with created_at <= awarded_at and nearest (closest prior)
  if (!matched && awardedYMD) {
    const awardedTs = new Date(w.awarded_at).getTime();
    const candidatesPrior = enriched
      .filter(f => f.createdTs && f.createdTs <= awardedTs)
      .sort((a, b) => b.createdTs - a.createdTs); // nearest prior first
    if (candidatesPrior.length) matched = candidatesPrior[0];
  }

  // 4) fallback: most recent form
  if (!matched && enriched.length) {
    enriched.sort((a, b) => (b.createdTs || 0) - (a.createdTs || 0));
    matched = enriched[0];
  }

  const displayScore = matched ? matched.score ?? "—" : "—";

  return (
    <tr
      key={w.id || i}
      className="border-t hover:bg-gray-50 cursor-pointer"
      onClick={() => setDetailModal(w)}
    >
      <td className="p-2 text-gray-700 font-medium">{i + 1}</td>

      <td className="p-2">
        <img
          src={w.shop_owner?.photo_url}
          alt={w.shop_owner?.owner_name}
          className="w-10 h-10 rounded-full"
        />
      </td>

      <td className="p-2">{w.shop_owner?.owner_name}</td>
      <td className="p-2">{w.shop_owner?.shop_name}</td>
      <td className="p-2 capitalize">{w.type}</td>

      <td className="p-2">
        {w.awarded_at ? new Date(w.awarded_at).toLocaleDateString("en-IN") : "—"}
      </td>

      <td className="p-2">
        {displayScore}
      </td>
    </tr>
  );
})}

          </tbody>
        </table>
      )}
    </div>
  </div>
)}

      {/* ===== Detail Modal ===== */}
      {detailModal && <DetailsModal selectedItem={detailModal} onClose={() => setDetailModal(null)} />}
    </div>
  );
};

export default AdminPanel;
